import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@components/common/Button';
import { useListRoles, useAssignRole, useRemoveRole, useUserRoles } from '@api/hooks/useRoles';
import type { Role } from '@schemas/api';

interface RoleSelectorProps {
  userId: string;
  onSuccess?: () => void;
}

export const RoleSelector = ({ userId, onSuccess }: RoleSelectorProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { data: rolesData } = useListRoles(0, 100);
  const { data: userRolesData } = useUserRoles(userId);
  const assignRoleMutation = useAssignRole();
  const removeRoleMutation = useRemoveRole();

  const allRoles = rolesData?.items || [];
  const userRoles = userRolesData?.roles || [];
  const assignedRoleIds = new Set(userRoles.map((r: any) => r.role_id));
  const availableRoles = allRoles.filter((r: Role) => !assignedRoleIds.has(r.id));

  const handleAssignRole = async (roleId: string) => {
    try {
      await assignRoleMutation.mutateAsync({ userId, roleId });
      setShowDropdown(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to assign role:', error);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    try {
      await removeRoleMutation.mutateAsync({ userId, roleId });
      onSuccess?.();
    } catch (error) {
      console.error('Failed to remove role:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Assigned Roles
        </h4>
        <div className="flex flex-wrap gap-2">
          {userRoles.length > 0 ? (
            userRoles.map((role: any) => (
              <div
                key={role.role_id}
                className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full"
              >
                <span className="text-sm">{role.role_name}</span>
                <button
                  onClick={() => handleRemoveRole(role.role_id)}
                  className="hover:text-blue-600 dark:hover:text-blue-400"
                  disabled={removeRoleMutation.isPending}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No roles assigned</p>
          )}
        </div>
      </div>

      {availableRoles.length > 0 && (
        <div className="relative">
          <Button
            variant="secondary"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            Add Role
          </Button>

          {showDropdown && (
            <div className="absolute top-full mt-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10">
              {availableRoles.map((role: Role) => (
                <button
                  key={role.id}
                  onClick={() => handleAssignRole(role.id)}
                  disabled={assignRoleMutation.isPending}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  {role.name}
                  {role.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {role.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
